<template>
  <transition name="slide-up">
    <div v-if="showInstallPrompt" class="pwa-install-banner">
      <div class="pwa-install-content">
        <div class="pwa-install-icon">
          <img src="/icons/android-icon-72x72.png" alt="Ordexa">
        </div>
        <div class="pwa-install-text">
          <h4>Instale o app do Ordexa</h4>
          <p>Na próxima sessão, toque no ícone e dite: a anotação fica pronta.</p>
        </div>
        <div class="pwa-install-actions">
          <button @click="installApp" class="btn-install">
            Instalar
          </button>
          <button @click="dismissPrompt" class="btn-dismiss">
            Agora não
          </button>
        </div>
        <button @click="permanentlyDismiss" class="btn-close">&times;</button>
      </div>
    </div>
  </transition>
</template>

<script>
import { canNudge, useNudge } from '@/utils/nudges'
import { track } from "@/utils/analytics";
import { canPrompt, isInAppBrowser, platform, promptInstall, wasInstalled } from "@/utils/appInstall";

// "Instale o app" on the phone, right after a note is saved (the moment the app proves its worth),
// or from the second day on. One self-opening prompt a day (nudges), never over a recording.
export default {
  name: 'PwaInstallPrompt',
  data() {
    return {
      showInstallPrompt: false,
      device: platform(),
    };
  },
  mounted() {
    this.$eventbus.on("Note/saved", this.afterNote);
    this.secondDay();
  },
  beforeUnmount() {
    this.$eventbus.off("Note/saved", this.afterNote);
    clearTimeout(this.timer);
  },
  methods: {
    eligible() {
      if (this.device === 'desktop' || !this.isMobile() || isInAppBrowser() || wasInstalled()) return false;
      try {
        if (localStorage.getItem('pwa-install-dismissed') === 'true') return false;
        if (Number(localStorage.getItem('pwa-install-dismissed-until') || 0) > Date.now()) return false;
      } catch (e) {
        // storage blocked (private window): treat as never dismissed
      }
      // Android shows it only when Chrome can install (or the menu can); iPhone always can, through Share.
      return this.device === 'ios' || canPrompt();
    },

    afterNote() {
      clearTimeout(this.timer);
      // The sample note of the first screen: the next step is a patient of their own, not installing.
      if (this.$route && this.$route.query.firstNote) return;
      // After the "salva" toast and any milestone (which uses up the day's prompt)
      this.timer = setTimeout(() => {
        let sample = false;
        try {
          sample = Date.now() - Number(sessionStorage.getItem('ordexa/sample-note-at') || 0) < 10000;
        } catch (e) {
          // ignore
        }
        if (!sample && this.eligible() && this.canInterrupt()) this.show('note');
      }, 3500);
    },

    // Not on the first day: half the screen covered right after signup made new accounts leave.
    secondDay() {
      const today = new Date().toISOString().slice(0, 10);
      let firstDay = null;
      try {
        firstDay = localStorage.getItem('pwa-first-day');
        if (!firstDay) localStorage.setItem('pwa-first-day', today);
      } catch (e) {
        return; // without storage we cannot tell the second day: stay quiet
      }
      if (!firstDay || firstDay === today) return;
      this.timer = setTimeout(() => {
        if (this.eligible() && this.canInterrupt()) this.show('return');
      }, 10000);
    },

    // Only on the signed-in app screens, never over the onboarding, public pages
    // (sent to patients) or a recording in progress.
    canInterrupt() {
      const path = window.location.pathname;
      if (/^\/(onboarding|signup|login|forgot-password|reset-password|entrar|consulta|sala|respirar|tarefa|assinar|escala|estimulo|ficha|agendar|instalar)(\/|$)/.test(path)) return false;
      return !document.querySelector('.recorder-recording') && canNudge();
    },

    show(moment) {
      useNudge();
      this.moment = moment;
      this.showInstallPrompt = true;
      track("pwa_prompt_shown", { ios: this.device === 'ios', moment });
    },

    isMobile() {
      return window.innerWidth <= 768;
    },

    async installApp() {
      this.showInstallPrompt = false;
      if (canPrompt()) {
        const outcome = await promptInstall('banner_' + (this.moment || ''));
        track("pwa_prompt_answer", { outcome });
        return;
      }
      track("pwa_prompt_answer", { outcome: 'steps' });
      this.$router.push({ path: '/instalar', query: { de: 'aviso' } });
    },

    dismissPrompt() {
      this.showInstallPrompt = false;
      track("pwa_prompt_answer", { outcome: 'later' });
      // Show again after 3 days
      try {
        localStorage.setItem('pwa-install-dismissed-until', Date.now() + (3 * 24 * 60 * 60 * 1000));
      } catch (e) {
        // ignore
      }
    },

    permanentlyDismiss() {
      this.showInstallPrompt = false;
      track("pwa_prompt_answer", { outcome: 'never' });
      try {
        localStorage.setItem('pwa-install-dismissed', 'true');
      } catch (e) {
        // ignore
      }
    }
  }
};
</script>

<style scoped>
.pwa-install-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #29B1CC, #1a8ea8);
  color: white;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  animation: slideUp 0.3s ease-out;
}

.pwa-install-content {
  display: flex;
  align-items: center;
  padding: 1em;
  max-width: 600px;
  margin: 0 auto;
  position: relative;
}

.pwa-install-icon {
  flex-shrink: 0;
  margin-right: 1em;
}

.pwa-install-icon img {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.pwa-install-text {
  flex: 1;
  margin-right: 1em;
}

.pwa-install-text h4 {
  margin: 0 0 0.25em 0;
  font-size: 1.1em;
  font-weight: 600;
}

.pwa-install-text p {
  margin: 0;
  font-size: 0.9em;
  opacity: 0.95;
}

.pwa-install-actions {
  display: flex;
  gap: 0.5em;
}

.btn-install {
  background: white;
  color: #29B1CC;
  border: none;
  padding: 0.6em 1.2em;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-install:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.3);
}

.btn-dismiss {
  background: transparent;
  color: white;
  border: 1px solid white;
  padding: 0.6em 1.2em;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-dismiss:hover {
  background: rgba(255, 255, 255, 0.1);
}

.btn-close {
  position: absolute;
  top: 0.5em;
  right: 0.5em;
  background: transparent;
  border: none;
  color: white;
  font-size: 1.5em;
  cursor: pointer;
  padding: 0.25em 0.5em;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.btn-close:hover {
  opacity: 1;
}

/* Mobile responsive */
@media (max-width: 600px) {
  .pwa-install-content {
    flex-direction: column;
    text-align: center;
  }

  .pwa-install-icon {
    margin-right: 0;
    margin-bottom: 0.5em;
  }

  .pwa-install-text {
    margin-right: 0;
    margin-bottom: 1em;
  }

  .pwa-install-actions {
    width: 100%;
    flex-direction: column;
  }

  .btn-install,
  .btn-dismiss {
    width: 100%;
  }
}

/* Animation */
.slide-up-enter-active, .slide-up-leave-active {
  transition: transform 0.3s ease;
}

.slide-up-enter-from, .slide-up-leave-to {
  transform: translateY(100%);
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
</style>
